/**
 * O filtro de menções, compartilhado por empresa (2026-10-17).
 *
 * Decisão da dona: o filtro da tela de Menções e o recorte de GERAÇÃO da
 * tela de Relatórios são UM estado só. Marcou um tema em menções, abriu
 * Relatórios, o tema está lá; e o contrário. Trocar de empresa zera —
 * cada empresa tem o seu, e o catálogo (tema, marca) também é dela.
 *
 * **Mora na casca, não na página.** O provedor é montado no
 * `AppShellProvider` com `key={companyId}`: as duas telas (que são rotas
 * irmãs, uma desmonta quando a outra monta) leem o mesmo estado, e o
 * `key` é o que faz trocar de empresa remontar o provedor e ler o
 * storage da nova — em vez de arrastar o filtro da anterior.
 *
 * **Persistência no formato do filtro SALVO** (`MentionFilterCriteria`,
 * v3), em `localStorage`, uma chave por empresa. Reusar o formato salvo
 * custa nada (a ida e a volta já existem e já têm teste) e herda a
 * decisão de "nada por nome": cada opção de catálogo vai como
 * `{label, ids}`, então renomear um tema não apaga o recorte guardado.
 * Fica FORA do storage, de propósito: a lista colada (`refFilter`, é um
 * atalho de 7 dias no servidor) e o filtro de upload (mora na URL de cada
 * rota). Tudo em `try/catch`: janela privada, cota estourada ou storage
 * bloqueado só tiram a persistência — o estado vale na aba.
 *
 * **Sem provedor, cai num estado local.** É o caso dos testes de unidade
 * que renderizam a página ou o hook sem a casca: eles continuam
 * funcionando, só que sem lembrar de nada entre montagens.
 *
 * **Este arquivo exporta SÓ o componente** (2026-10-25, achado 26 da
 * revisão de 2026-10-24): Context, tipos, storage e o hook
 * `useMentionFilterStore` moram em `mention-filter-context.ts`, pelo
 * mesmo motivo que separou `app-shell-context.ts` da casca — o Fast
 * Refresh só preserva o que está montado num módulo que exporta só
 * componentes, e aqui a falha era silenciosa (as telas paravam de
 * compartilhar o filtro até um F5). `shell/fast-refresh-boundary.test.ts`
 * guarda a fronteira.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  hasVisited,
  markVisited,
  readStored,
  StoreContext,
  writeStored,
  type IdResolverFn,
  type SharedMentionFilters,
  type StoreValue,
} from "@/features/mentions/mention-filter-context";

export function MentionFilterProvider({
  companyId,
  children,
}: {
  companyId: string | null;
  children: ReactNode;
}) {
  // Lido no PRIMEIRO render, e não num efeito: lido depois, o filtro
  // restaurado dispararia uma segunda consulta logo após a primeira.
  const [state, setStateRaw] = useState<SharedMentionFilters>(() =>
    readStored(companyId)
  );
  // Primeira visita (2026-10-26): a empresa nunca teve filtro aqui, e a
  // tela de menções abre com a janela de data padrão. A primeira ESCRITA
  // QUE MUDA ALGO — a janela mesma, um clique, o que for — encerra isso e
  // deixa a marca de visita no storage, porque "limpei tudo" não deixa
  // filtro guardado e a próxima visita não pode ser tratada como a
  // primeira. Escrita vazia (a função devolve o estado que recebeu) NÃO
  // conta: o efeito dos concorrentes faz uma dessas na montagem de toda
  // empresa sem concorrente, e se ela chegasse antes do último dado a
  // janela padrão nunca entraria — corrida real, medida pela ordem das
  // respostas. A conferência lê o estado pelo `stateRef` (o de agora,
  // fora do updater) só pra decidir a marca; a escrita em si continua
  // funcional, composta pelo React.
  const [fresh, setFresh] = useState(() => !hasVisited(companyId));
  const stateRef = useRef(state);
  stateRef.current = state;
  const setState = useCallback(
    (update: (current: SharedMentionFilters) => SharedMentionFilters) => {
      if (update(stateRef.current) !== stateRef.current) {
        setFresh(false);
        markVisited(companyId);
      }
      setStateRaw(update);
    },
    [companyId]
  );
  const resolverRef = useRef<IdResolverFn | null>(null);

  // Grava a cada mudança do que se persiste. `refFilter` fica de fora da
  // lista de dependências de propósito (não é gravado).
  useEffect(() => {
    writeStored(companyId, state, resolverRef.current);
  }, [companyId, state.filters, state.query, state.searchIn, state.scope, state.savedIds]);

  const value = useMemo<StoreValue>(
    () => ({ state, fresh, setState, companyId, resolverRef }),
    [state, fresh, setState, companyId]
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
